import { Button } from '@odin-labs/components';
import { useGetJobsiteConfigInfoQuery } from 'apollo/generated/client-operations';
import { TrashIcon } from 'components/icons';
import { Form } from 'components/form';
import { LoadingError } from 'components/loadingError';
import { JobsiteConfigTabProps } from 'containers/jobsiteConfiguration/types';
import { useJobsiteConfigTabForm } from 'containers/jobsiteConfiguration/helpers/useJobsiteConfigTabForm';
import React from 'react';
import { useBoolean } from 'utils';
import { ArchiveJobsiteModal } from 'containers/jobsiteConfiguration/tabs/modals';
import {
  type EditJobsiteConfigTabFormData,
  getDefaultValues,
  getDropdownsOptions,
  getFormInputs,
  getJobsiteUpdateInput,
} from './JobsiteInfoTab.forms';

export function JobsiteInfoTab(
  props: JobsiteConfigTabProps,
): React.ReactElement {
  const { jobsiteId, tab, onTabApiChange, onIsDirtyChange } = props;

  const {
    value: isArchiveJobsiteModalOpen,
    setTrue: openArchiveJobsiteModal,
    setFalse: closeArchiveJobsiteModal,
  } = useBoolean(false);

  const {
    data: jobsiteConfigData,
    loading: jobsiteConfigLoading,
    error: jobsiteConfigError,
    refetch,
  } = useGetJobsiteConfigInfoQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: { jobsiteId },
  });

  const jobsite = jobsiteConfigData?.getJobsite;
  const userData = jobsiteConfigData?.getCurrentSession.user;

  React.useEffect(
    () =>
      onTabApiChange({
        refetchData: refetch,
        loading: jobsiteConfigLoading,
        jobsiteName: jobsite?.name,
      }),
    [onTabApiChange, refetch, jobsiteConfigLoading, jobsite?.name],
  );

  const { formRef, onSubmit } =
    useJobsiteConfigTabForm<EditJobsiteConfigTabFormData>({
      onTabApiChange,
      onIsDirtyChange,
      tab,
      refetch,
      getInput: (data, dirtyFields) =>
        getJobsiteUpdateInput(jobsite, data, dirtyFields),
    });

  const { formInputs, defaultValues } = React.useMemo(() => {
    const dropdownsOptions = getDropdownsOptions({ userData, jobsite });
    return {
      formInputs: getFormInputs({ jobsite, ...dropdownsOptions }),
      defaultValues: getDefaultValues({ jobsite, ...dropdownsOptions }),
    };
  }, [userData, jobsite]);

  if (!jobsite) {
    return <LoadingError loading />;
  }

  if (jobsiteConfigError) {
    return <LoadingError error={jobsiteConfigError} />;
  }

  return (
    <div className="odin-relative">
      {jobsiteConfigLoading && (
        <div className="table-backdrop">
          <LoadingError loading />
        </div>
      )}
      <Form
        ref={formRef}
        inputs={formInputs}
        onSubmit={onSubmit}
        onIsDirtyChange={onIsDirtyChange}
        defaultValues={defaultValues}
        className="odin-pr-3"
        inputsContainerClassName="odin-space-y-5 sm:odin-space-y-9 odin-divide-y odin-divide-gray-200 odin-pb-9"
        resetTrigger={jobsite}
        renderBelow={(): React.ReactElement => (
          <div className="odin-flex odin-justify-end odin-pb-9">
            <Button
              theme="danger"
              icon={TrashIcon}
              text="Archive Jobsite"
              onClick={openArchiveJobsiteModal}
            />
            <ArchiveJobsiteModal
              isOpen={isArchiveJobsiteModalOpen}
              closeModal={closeArchiveJobsiteModal}
              jobsite={jobsite}
            />
          </div>
        )}
      />
    </div>
  );
}
